import { motion } from 'framer-motion';
import ls from 'localstorage-slim';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setIsAnimated } from '../../../redux/cache-slice';
import { fetchCarDetails } from '../../../redux/leaseApp/lease-app-slice';
import { EDevice } from '../../../redux/sequence/enums';
import { useAppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import { Partner, Type } from '../../../types/instant-lease-api';
import { formatGermanPrice, getPartnerText } from '../../../utils/getter';
import { CapitalizeFirstLetter } from '../../../utils/helper';
import Button from '../../b2b/components/button/button';
import UnknownCar from '../../b2b/components/images/unknown-car.png';
import PartnerLogo from '../../b2b/components/partner-logo/partner-logo';
import ButtonGroup from '../../b2b/molecules/button-group/button-group';
import {
  getFuelType,
  getGearBoxType,
  getVehicleBody,
} from '../car-details/car-data-mapper';
import LeaseRow from '../lease-row/leaseRow';
import PricingDetails from '../pricing-details/pricing-details';
import './bootstrap.css';

export interface FlexCarDetails {
  duration: string;
  gross: number;
  mileage: number;
  net: number;
  title: string;
  vehicleImageUrl: string;
  type: Type.B2C | Type.B2B;
}

const Bootstrap: React.FC = () => {
  // Lease Application Storage
  const { carData } = useSelector((state: RootState) => state.leaseApp);

  const [isExiting, setIsExiting] = useState(false);
  const [loadingCarDetailError, setLoadingCarDetailError] =
    useState<Error | null>(null);

  // History hook
  const navigate = useNavigate();

  // Dispatch hook
  const dispatch = useAppDispatch();

  const { device } = useSelector((state: RootState) => state.sequence);

  // Handle on button click
  const handleOnClick = useCallback(
    (path: string) => navigate(path),
    [navigate],
  );

  // Format price with german price format
  const { format } = formatGermanPrice();

  const { pathname } = useLocation();

  // Current URL
  const searchParams = new URLSearchParams(document.location.search);

  // Get previous URL from browser history
  const mobilityPortalUrl =
    window.history.length > 1 ? document.referrer : null;

  //Save the previous URL to local storage for later use
  useEffect(() => {
    if (mobilityPortalUrl) {
      ls.set('mobilityPortalUrl', mobilityPortalUrl);
    }
  }, [mobilityPortalUrl]);

  // URL Variables
  const partner = Partner.valueOf(searchParams.get('partner')?.toUpperCase());
  const publicationId = searchParams.get('publicationId');
  const pricingId = searchParams.get('pricingId');
  const offerId = searchParams.get('offerId');

  // if no offer or carData move to no offer page
  useEffect(() => {
    if (!publicationId && !pricingId && !offerId) {
      navigate('/login');
    }
  }, [partner, offerId, pricingId, publicationId, navigate]);

  // Get current car regardless of saved data
  useEffect(() => {
    const fetchCurrentCarDetails = async () => {
      try {
        await dispatch(
          fetchCarDetails({ partner, publicationId, pricingId, offerId }),
        ).unwrap();
      } catch (error) {
        console.error('Error fetching car details for bootstrap page:', error);
        setLoadingCarDetailError(
          error instanceof Error
            ? error
            : new Error('Error fetching car details for bootstrap page'),
        );
      }
    };

    if ((publicationId && pricingId) || offerId) {
      ls.set('bootstrapPage', true);
      fetchCurrentCarDetails();
    }
  }, [partner, offerId, pricingId, publicationId, dispatch, pathname]);

  const handleContinue = () => {
    setIsExiting(true);
    dispatch(setIsAnimated(true));

    // Wait for the exit animation + a tiny buffer
    setTimeout(() => {
      navigate('/login');
    }, 450); // matches motion.div transition (0.4s)
  };

  const carInformation = useMemo(
    () => [
      {
        label: 'Fahrzeugart',
        value:
          carData?.carVehicleState === 'USED' ? 'Gebrauchtwagen' : 'Neuwagen',
      },
      {
        label: 'Beschleunigung',
        value: carData?.carAccelerationToHundredKph
          ? `${format(Number(carData.carAccelerationToHundredKph)).replace(
              '€',
              '',
            )}s (0-100km/h)`
          : '-',
      },
      {
        label: 'Karosserie',
        value: carData?.carBodyType && getVehicleBody(carData?.carBodyType),
      },
      { label: 'Farbe', value: carData?.carColorName ?? '-' },
      {
        label: 'Kraftstoff',
        value: CapitalizeFirstLetter(
          ((carData?.carFuelType && getFuelType(carData?.carFuelType)) || '')
            .toLowerCase()
            .replace('_', ' ') ?? '-',
        ),
      },
      { label: 'Türen', value: carData?.carNumberOfDoors ?? '-' },
      {
        label: 'Getriebe',
        value: CapitalizeFirstLetter(
          (
            (carData?.carTransmissionType &&
              getGearBoxType(carData?.carTransmissionType)) ||
            ''
          )
            .toLowerCase()
            .replace('_', ' ') ?? '-',
        ),
      },
      {
        label: 'Effizienzklasse',
        value: carData?.efficiencyClass ?? '-',
      },
      {
        label: 'Batterie Kapazität',
        value: carData?.batteryCapacity
          ? `${carData.batteryCapacity} KWh`
          : '-',
      },
      {
        label: 'Elektrische Reichweite',
        value: carData?.electricalRange ? `${carData.electricalRange} km` : '-',
      },
      {
        label: 'Leistung',
        value:
          carData?.carPowerKiloWatt && carData?.carHorsePower
            ? `${carData.carPowerKiloWatt} kW | ${carData.carHorsePower} PS`
            : '-',
      },
      {
        label: 'max. Geschwindigkeit',
        value: carData?.carMaxSpeed ? `${carData.carMaxSpeed} km/h` : '-',
      },
    ],
    [carData, format],
  );

  // If the car details fail to load, navigate to the error page.
  if (loadingCarDetailError) {
    console.error(
      'Error during loading of car details, navigating to error page',
    );
    navigate('/invalid-offer-error');
  }

  return (
    <motion.div
      initial={{ opacity: 1, y: 0 }}
      animate={isExiting ? { opacity: 0, y: -40 } : { opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
    >
      <div className='header'>
        <div role='presentation'>
          <PartnerLogo className='image' />
        </div>
      </div>

      <div className='bootstrap grid-template container'>
        <div className='template-body-wide'>
          {carData && (
            <div className='subheader'>
              <PricingDetails visibility />
              <div className='car-details'>
                <div className='offer-details'>
                  <p className='offer content-normal'>
                    {carData?.carVehicleState === 'USED'
                      ? `Gebrauchtwagen ${
                          carData?.carUsedMileage
                            ? `| ${carData?.carUsedMileage} km`
                            : ''
                        }`
                      : 'Neu'}{' '}
                    |{' '}
                    {CapitalizeFirstLetter(
                      (
                        (carData?.carFuelType &&
                          getFuelType(carData?.carFuelType)) ||
                        ''
                      )
                        .toLowerCase()
                        .replace('_', ' '),
                    )}
                  </p>{' '}
                  {/* <p className='small-text dark-grey consumption space'>
                    {carData?.carCo2} g/km (komb.)*,{' '}
                    {carData?.carFuelEconomyCombined ||
                      carData?.carPowerConsumptionCombined ||
                      0}
                    /100 km (komb.)* ({carData?.carConsumptionAndEmissionTest})
                  </p> */}
                  <div className='lease-info'>
                    <p className='content-bold'>Vertragsdaten</p>{' '}
                    <div className='lease-details'>
                      {!!carData?.totalListPrice && (
                        <LeaseRow
                          label='Listenpreis'
                          value={format(carData?.totalListPrice)}
                        />
                      )}
                      {carData?.duration && (
                        <LeaseRow
                          label='Laufzeit'
                          value={` ${carData.duration} Monate `}
                        />
                      )}
                      {carData?.mileageYear && (
                        <LeaseRow
                          label='Laufleistung'
                          value={` ${Math.floor(
                            carData?.mileageYear || 0,
                          ).toLocaleString('de-DE')} km/Jahr `}
                        />
                      )}
                      {!!carData?.exceedingMileagePrice && (
                        <LeaseRow
                          label='Mehrkilometer-Kosten'
                          value={` ${format(
                            carData?.exceedingMileagePrice,
                          ).replace('€', '')} ct/km `}
                        />
                      )}
                      {!!carData?.inferiorMileagePrice && (
                        <LeaseRow
                          label='Minderkilometer-Erstattung'
                          value={` ${format(
                            carData?.inferiorMileagePrice,
                          ).replace('€', '')} ct/km `}
                        />
                      )}
                    </div>
                  </div>
                  <div className='lease-info'>
                    <p className='content-bold'>Einmalkosten</p>
                    <div className='lease-details'>
                      {!!carData?.downPayment && (
                        <LeaseRow
                          label='Sonderzahlung'
                          value={format(carData?.downPayment)}
                        />
                      )}

                      {!!carData?.additionalCost?.logistics && (
                        <LeaseRow
                          label='Logistik'
                          value={format(carData?.additionalCost?.logistics)}
                        />
                      )}

                      {!!carData?.additionalCost?.returnFreight && (
                        <LeaseRow
                          label='Rückholkosten'
                          value={format(carData?.additionalCost?.returnFreight)}
                        />
                      )}

                      {!!carData?.additionalCost?.registration && (
                        <LeaseRow
                          label='Zulassungskosten'
                          value={format(carData?.additionalCost?.registration)}
                        />
                      )}

                      {!!carData?.additionalCost?.freight && (
                        <LeaseRow
                          label={getPartnerText('additionalCost_freight')}
                          value={format(carData?.additionalCost?.freight)}
                        />
                      )}
                    </div>
                  </div>
                  {carData?.services && (
                    <div className='lease-info last'>
                      <p className='content-bold'>Monatliche Kosten</p>{' '}
                      <div className='lease-details'>
                        <LeaseRow
                          label='Finanz-Leasingrate'
                          value={
                            carData?.services?.finance
                              ? format(carData?.services?.finance)
                              : '-'
                          }
                        />
                        <LeaseRow
                          label='Technik-Service'
                          value={
                            carData?.services?.technic
                              ? format(carData?.services?.technic)
                              : '-'
                          }
                        />
                        <LeaseRow
                          label='Versicherungs-Service'
                          value={
                            carData?.services?.insurance
                              ? format(carData?.services?.insurance)
                              : '-'
                          }
                        />
                        <LeaseRow
                          label='Fuel & Charge Karte'
                          value={
                            carData?.services?.fuelCard
                              ? format(carData?.services?.fuelCard || 0)
                              : '-'
                          }
                        />
                        <LeaseRow
                          label='Rückhol-Service'
                          value={
                            carData?.services?.returnFreight
                              ? format(carData?.services?.returnFreight || 0)
                              : '-'
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className='car-visual'>
                  <div
                    className='car-details-image'
                    style={{
                      backgroundImage: `url(${
                        _.isEmpty(carData?.defaultImage)
                          ? UnknownCar
                          : carData?.defaultImage
                      })`,
                    }}
                  />
                  <div className='car-information'>
                    {carInformation
                      .filter((info) => info.value)
                      .map(({ label, value }, index) => (
                        <div key={index}>
                          <p className='input-text info-title'>{label}</p>
                          <p className='content-bold'>{value}</p>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              {device === EDevice.MOBILE ? (
                <ButtonGroup
                  type='back-next'
                  className='back-next medium'
                  buttonOneProps={{
                    type: 'button',
                    theme: 'primary',
                    children: 'Weiter',
                  }}
                  onClick={() => {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                    dispatch(setIsAnimated(true));
                    handleOnClick('/login');
                  }}
                ></ButtonGroup>
              ) : (
                <Button
                  type='button'
                  className='button-text long'
                  theme='primary'
                  onClick={() => {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                    dispatch(setIsAnimated(true));
                    handleContinue();
                    handleOnClick('/login');
                  }}
                >
                  Weiter
                </Button>
              )}
            </div>
          )}

          {!carData && (
            <img src={UnknownCar} alt='car' className='car-list unknown-car' />
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Bootstrap;
